import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, SearchIcon } from '@heroicons/react/outline';
import { cn } from './cn';

function selectionSummary(options, value, placeholder) {
  if (value.length === 0) return placeholder;

  if (value.length === 1) {
    const selectedOption = options.find((option) => option.value === value[0]);
    return selectedOption?.label || '1 selected';
  }

  return `${value.length} selected`;
}

function useAnchoredPanel(open, anchorRef, panelRef) {
  const [style, setStyle] = useState({ visibility: 'hidden' });

  const positionPanel = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor || typeof window === 'undefined') return;

    const rect = anchor.getBoundingClientRect();
    const viewportPadding = 8;
    const gap = 4;
    const width = Math.min(rect.width, window.innerWidth - viewportPadding * 2);
    const left = Math.max(
      viewportPadding,
      Math.min(rect.left, window.innerWidth - width - viewportPadding)
    );
    const panelHeight = panelRef.current?.offsetHeight || 0;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenAbove = panelHeight > spaceBelow && spaceAbove > spaceBelow;
    const top = shouldOpenAbove
      ? Math.max(viewportPadding, rect.top - panelHeight - gap)
      : rect.bottom + gap;

    setStyle({
      left,
      top,
      width,
      availableHeight: Math.max(0, Math.max(spaceBelow, spaceAbove)),
      visibility: 'visible',
    });
  }, [anchorRef, panelRef]);

  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    window.addEventListener('resize', positionPanel);
    window.addEventListener('scroll', positionPanel, true);

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(positionPanel);
    if (anchorRef.current) resizeObserver?.observe(anchorRef.current);
    if (panelRef.current) resizeObserver?.observe(panelRef.current);

    return () => {
      window.removeEventListener('resize', positionPanel);
      window.removeEventListener('scroll', positionPanel, true);
      resizeObserver?.disconnect();
    };
  }, [anchorRef, open, panelRef, positionPanel]);

  return { style, positionPanel };
}

function MultiSelectContent({
  open,
  options,
  value,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
  id,
  ariaLabel,
  className,
  onOpenChange,
  autoOpen,
  selectionLabel,
}) {
  const [query, setQuery] = useState('');
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);
  const summaryId = useId();
  const previousOpen = useRef(open);
  const didAutoOpen = useRef(false);
  const { style: panelStyle, positionPanel } = useAnchoredPanel(open, anchorRef, panelRef);

  useEffect(() => {
    if (previousOpen.current === open) return;
    previousOpen.current = open;
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (autoOpen && !disabled && !didAutoOpen.current) {
      didAutoOpen.current = true;
      toggleRef.current?.click();
    }
  }, [autoOpen, disabled]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) =>
      `${option.label} ${option.description || ''}`.toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [options, query]);

  useLayoutEffect(() => {
    if (open) positionPanel();
  }, [filteredOptions.length, open, positionPanel]);

  const summary = selectionLabel ?? selectionSummary(options, value, placeholder);
  const toggleLabel = `Toggle ${ariaLabel || placeholder} options`;
  const { availableHeight, ...panelPositionStyle } = panelStyle;
  const optionsMaxHeight = Math.min(availableHeight ?? 256, 256);

  const panel = (
    <Combobox.Options
      ref={panelRef}
      style={{ ...panelPositionStyle, maxHeight: optionsMaxHeight }}
      className="fixed z-50 m-0 list-none overflow-y-auto rounded-bun-lg border border-bun-line bg-bun-surface p-[6px] font-instrument text-bun-text shadow-bun-menu"
    >
      {filteredOptions.length === 0 ? (
        <li className="px-[10px] py-3 text-bun-body font-book text-bun-stone">{emptyText}</li>
      ) : (
        filteredOptions.map((option) => (
          <Combobox.Option key={option.value} value={option.value}>
            {({ active, selected }) => (
              <div
                className={cn(
                  'flex min-h-11 sm:min-h-0 cursor-pointer items-center gap-[10px] rounded-[8px] px-[10px] py-2 text-bun-body',
                  (active || selected) && 'bg-bun-wash'
                )}
              >
                {option.icon && (
                  <span aria-hidden="true" className="shrink-0">
                    {option.icon}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-book text-bun-text">{option.label}</span>
                  {option.description && (
                    <span className="block truncate text-bun-label font-book normal-case tracking-normal text-bun-stone">
                      {option.description}
                    </span>
                  )}
                </span>
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {selected && <CheckIcon aria-hidden="true" className="h-4 w-4 text-bun-blue" />}
                </span>
              </div>
            )}
          </Combobox.Option>
        ))
      )}
    </Combobox.Options>
  );

  return (
    <div ref={anchorRef} className={cn('relative w-full font-instrument', className)}>
      <span id={summaryId} className="sr-only">
        {value.length === 0 ? 'No options selected' : summary}
      </span>
      <div
        className={cn(
          'flex min-h-11 sm:min-h-0 w-full items-center gap-2 rounded-bun border border-bun-line bg-bun-surface px-3 text-bun-body font-book text-bun-text outline-none',
          'focus-within:border-bun-blue focus-within:ring-[3px] focus-within:ring-bun-ring',
          disabled && 'cursor-not-allowed bg-bun-mist text-bun-stone'
        )}
      >
        <SearchIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-bun-stone" />
        <div className="relative min-w-0 flex-1">
          <Combobox.Input
            id={id}
            aria-label={ariaLabel}
            aria-describedby={summaryId}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClick={() => {
              if (!open) toggleRef.current?.click();
            }}
            placeholder={open ? searchPlaceholder : undefined}
            className={cn(
              'w-full min-w-0 border-0 bg-transparent py-[9px] text-bun-body font-book text-bun-text outline-none focus:ring-0',
              'placeholder:text-bun-faint disabled:cursor-not-allowed disabled:text-bun-stone'
            )}
          />
          {!open && query.length === 0 && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-0 flex items-center truncate text-bun-body font-book',
                value.length === 0 ? 'text-bun-faint' : 'text-bun-text',
                disabled && 'text-bun-stone'
              )}
            >
              {summary}
            </span>
          )}
        </div>
        <Combobox.Button
          ref={toggleRef}
          type="button"
          aria-label={toggleLabel}
          className="-mr-2 inline-flex min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 items-center justify-center rounded-bun text-bun-stone outline-none"
        >
          <ChevronDownIcon
            aria-hidden="true"
            className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
          />
        </Combobox.Button>
      </div>

      {open && typeof document !== 'undefined' && createPortal(panel, anchorRef.current?.closest('[data-bun-popover-panel], [data-bun-dialog-panel], [role="dialog"]') || document.body)}
    </div>
  );
}

/**
 * Controlled Bun searchable multi-select. Values are primitive option values;
 * keeping them separate from `options` preserves selections while option data
 * is temporarily incomplete. The listbox portals to avoid table overflow
 * clipping while its input remains the keyboard-focusable primary control.
 */
export default function SearchableMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options',
  searchPlaceholder = 'Search options',
  emptyText = 'No options found',
  disabled = false,
  name,
  id,
  'aria-label': ariaLabel,
  className,
  onOpenChange,
  autoOpen = false,
  selectionLabel,
}) {
  return (
    <Combobox
      value={value}
      onChange={onChange}
      multiple
      disabled={disabled}
      name={name}
    >
      {({ open }) => (
        <MultiSelectContent
          open={open}
          options={options}
          value={value}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          emptyText={emptyText}
          disabled={disabled}
          id={id}
          ariaLabel={ariaLabel}
          className={className}
          onOpenChange={onOpenChange}
          autoOpen={autoOpen}
          selectionLabel={selectionLabel}
        />
      )}
    </Combobox>
  );
}
